import React, { useState, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import _ from 'underscore'

import SiteMessage from 'pages/shared/SiteMessage'
import { SiteMessageDetail } from 'types/SiteMessageDetail'

import useSiteMessageBanners from 'hooks/useSiteMessageBanners'

import { color } from 'styles/colors'
import { screenWidthBreakpoints, screenSize } from 'styles/shared'

import Analytics from 'helpers/analytics'

const SiteMessageBanners = () => {
  const dismissedBannersStorageKey = 'dismissedSiteMessageBanners'
  const { siteMessageBanners } = useSiteMessageBanners()
  const [visibleSiteMessageBanners, setVisibleSiteMessageBanners] = useState<SiteMessageDetail[]>([])

  const onActionButtonClickCallback = () => {
    const eventData = { category: 'Site Message Banner', action: 'Clicked action button' }

    Analytics.track(eventData)
  }

  const onImageClickedCallback = () => {
    const eventData = { category: 'Site Message Banner', action: 'Clicked image' }

    Analytics.track(eventData)
  }

  const onTextClickedCallback = () => {
    const eventData = { category: 'Site Message Banner', action: 'Clicked text link' }

    Analytics.track(eventData)
  }

  const getDismissedBanners = useCallback(() => {
    const dismissedBanners = localStorage.getItem(dismissedBannersStorageKey)

    return (dismissedBanners && JSON.parse(dismissedBanners)) || []
  }, [])

  const dismissBanner = useCallback((siteMessage: SiteMessageDetail) => {
    const dismissedBanners = _.reject(getDismissedBanners(), (dismissedBanner: SiteMessageDetail) => dismissedBanner.id === siteMessage.id)

    dismissedBanners.push(_.pick(siteMessage, 'id', 'updatedAt'))

    localStorage.setItem(dismissedBannersStorageKey, JSON.stringify(dismissedBanners))

    updateVisibleSiteMessageBanners()
  }, [siteMessageBanners]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateVisibleSiteMessageBanners = useCallback(() => {
    const dismissedBanners = getDismissedBanners()

    const visibleBanners = _.reject(siteMessageBanners ?? [], (banner: SiteMessageDetail) => (
      _.any(dismissedBanners, (dismissedBanner: SiteMessageDetail) => (
        banner.id === dismissedBanner.id && banner.updatedAt === dismissedBanner.updatedAt
      ))
    ))

    setVisibleSiteMessageBanners(visibleBanners)
  }, [siteMessageBanners, getDismissedBanners])

  useEffect(
    () => updateVisibleSiteMessageBanners(),
    [siteMessageBanners, updateVisibleSiteMessageBanners]
  )

  return (
    <>
      {_.map(visibleSiteMessageBanners, (siteMessageBanner) => (
        <S.SiteMessage key={siteMessageBanner.id} messageType={siteMessageBanner.messageType}>
          <S.Close role="button" aria-label="Close" src="/img/close.svg" onClick={() => dismissBanner(siteMessageBanner)} />
          <S.Body
            siteMessage={siteMessageBanner}
            onActionButtonClickCallback={onActionButtonClickCallback}
            onImageClickCallback={onImageClickedCallback}
            onTextClickCallback={onTextClickedCallback}
            showDescription
          />
        </S.SiteMessage>
      ))}
    </>
  )
}

export default SiteMessageBanners

const padding = 20
const S = {
  SiteMessage: styled(SiteMessage)`
    display: flex;
    flex-direction: column;

    border-bottom: 1px solid ${color('border')};
  `,
  Close: styled.img`
    align-self: flex-end;
    height: 35px;
    width: 35px;
    margin: 0 10px -40px -${padding}px;
    cursor: pointer;
    z-index: 1000;
  `,
  Body: styled(SiteMessage.Body)`
    padding: ${padding}px;
    max-width: ${screenWidthBreakpoints.maxLarge}px;

    @media ${screenSize.small} {
      padding-top: 10px;
    }
  `
}
